import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Dropdown,
    DropdownItemProps,
    DropdownProps,
    Form,
    Modal,
    Popup,
    Select,
} from "semantic-ui-react";
import { toast } from "../../..";
import { ICountryProps } from "../../../models/country";
import staticFilterOptions from "../../../components/FormsOverviewTable/staticFilterOptions.json";
import { IForm } from "../../../models/form";
import { getCountries } from "../../../services/countriesService";
import { getBlocks, getQuestionsFromSection } from "../../../services/formsService";
import { getFormSectionsAsProps } from "../../../services/sectionsService";
import { IQuestion } from "../../../models/question";
import { ITableDto } from "../../../models/table";
import { createTable, editTable } from "../../../services/tablesService";
import { WarningModal } from "../../../components/WarningModal/WarningModal";

interface IProps {
    generateModalOpen: boolean;
    tableToEdit?: ITableDto;
    setTableToEdit: (table: ITableDto | undefined) => void;
    closeGenerateModal: () => void;
    allFormsWithAttributes: IForm[];
    refreshTables: () => void;
}

export const GenerateModal: React.FC<IProps> = ({
    generateModalOpen,
    tableToEdit,
    setTableToEdit,
    closeGenerateModal,
    allFormsWithAttributes,
    refreshTables,
}) => {
    const [formToGenerate, setFormToGenerate] = useState<string>(tableToEdit?.form ?? "");
    const [yearToGenerate, setYearToGenerate] = useState<string>(tableToEdit?.year ?? "");
    const [sectionToGenerate, setSectionToGenerate] = useState<string>(tableToEdit?.section ?? "");
    const [questionsToGenerate, setQuestionsToGenerate] = useState<string[]>(
        tableToEdit?.questions ?? []
    );
    const [countriesToGenerate, setCountriesToGenerate] = useState<string[]>(
        tableToEdit?.countries ?? []
    );

    const [formAsDropdownProps, setFormAsDropdownProps] = useState<DropdownItemProps[]>([]);
    const [yearAsDropdownProps, setYearAsDropdownProps] = useState<DropdownItemProps[]>(
        staticFilterOptions.year
    );
    const [sectionsAsDropdownProps, setSectionsAsDropdownProps] = useState<DropdownItemProps[]>([]);
    const [questionsAsDropdownProps, setQuestionsAsDropdownProps] = useState<DropdownItemProps[]>(
        []
    );
    const [countriesAsDropdownProps, setCountriesAsDropdownProps] = useState<ICountryProps[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
    const [changeSource, setChangeSource] = useState<"form" | "year" | "section" | "">("");

    const handleFormSelection = useCallback(() => {
        const formsAsProps: DropdownItemProps[] = [];
        allFormsWithAttributes
            .filter(alwa => alwa.categoryObj?.name !== "Generation Capacity")
            .forEach(form => {
                formsAsProps.push({
                    key: form.id,
                    text: form.name,
                    value: form.id,
                });
            });
        setFormAsDropdownProps(formsAsProps);
    }, [allFormsWithAttributes]);

    useEffect(() => {
        handleFormSelection();
    }, [handleFormSelection]);

    const handleYearSelection = useCallback(() => {
        const yearsAsProps: DropdownItemProps[] = [];
        allFormsWithAttributes.forEach(form => {
            if (form.id === formToGenerate) {
                const year = new Date(form.timePublished).getFullYear().toString();
                yearsAsProps.push({
                    key: year,
                    text: year,
                    value: year,
                });
            }
        });
        setYearAsDropdownProps(yearsAsProps);
    }, [allFormsWithAttributes, formToGenerate]);

    useEffect(() => {
        handleYearSelection();
    }, [handleYearSelection]);

    const fetchSectionsAsDropdownProps = useCallback(async () => {
        try {
            if (formToGenerate && yearToGenerate && formAsDropdownProps.length > 0) {
                const formName =
                    formAsDropdownProps.find(f => f.value === formToGenerate)?.text?.toString() ??
                    "";
                const sections = await getFormSectionsAsProps(formName, yearToGenerate);
                setSectionsAsDropdownProps(sections);
            } else {
                setSectionsAsDropdownProps([]);
            }
        } catch (err) {
            toast("Error fetching sections", false, 2000);
        }
    }, [formToGenerate, yearToGenerate, formAsDropdownProps]);

    useEffect(() => {
        fetchSectionsAsDropdownProps();
    }, [fetchSectionsAsDropdownProps]);

    const fetchQuestions = useCallback(async () => {
        try {
            if (!sectionToGenerate) {
                setQuestionsAsDropdownProps([]);
            } else {
                const res = await getQuestionsFromSection(sectionToGenerate);
                setQuestionsAsDropdownProps(
                    res
                        .filter((q: IQuestion) => q.type !== "TableInput")
                        .map((q: IQuestion) => ({
                            key: q.id,
                            text: `Q${q.orderNumber}: ${q.text}`,
                            value: q.id,
                        }))
                        .sort((a: IQuestion, b: IQuestion) => a.text.localeCompare(b.text))
                );
            }
        } catch (error) {
            toast("Error fetching questions", false, 2000);
        }
    }, [sectionToGenerate]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const fetchCountriesAsDropdownProps = useCallback(async () => {
        try {
            const block = await getBlocks();
            const countries = await getCountries(
                block.find(x => x.name === "Network codes monitoring")?.id
            );
            if (Array.isArray(countries)) {
                const countriesAsProps: ICountryProps[] = [];
                countries.sort((a, b) => a.name.localeCompare(b.name));
                countries.forEach(country =>
                    countriesAsProps.push({
                        key: country.id,
                        text: country.name,
                        value: country.id,
                        eu: country.isEu ? 1 : 0,
                    })
                );

                const selectAll: ICountryProps = {
                    key: "selectAll",
                    text: "Select all",
                    value: "selectAll",
                    eu: 0,
                };
                const EU: ICountryProps = {
                    key: "EU",
                    text: "European Union",
                    value: "EU",
                    eu: 0,
                };
                countriesAsProps.unshift(EU);
                countriesAsProps.unshift(selectAll);
                setCountriesAsDropdownProps(countriesAsProps);
            }
        } catch (error) {
            toast("Error fetching countries", false, 2000);
        }
    }, []);

    useEffect(() => {
        fetchCountriesAsDropdownProps();
    }, [fetchCountriesAsDropdownProps]);

    const handleSelectCountriesToGenerate = (
        _e: React.SyntheticEvent<HTMLElement, Event>,
        data: DropdownProps
    ) => {
        if ((data.value as string).includes("selectAll")) {
            const allCountries: string[] = countriesAsDropdownProps
                .slice(2, countriesAsDropdownProps.length)
                .filter(item => item.value)
                .map(item => item.value as string);
            setCountriesToGenerate(allCountries);
        } else if ((data.value as string).includes("EU")) {
            const euCountries: string[] = countriesAsDropdownProps
                .slice(2, countriesAsDropdownProps.length)
                .filter(item => item.eu === 1)
                .filter(item => item.value)
                .map(item => item.value as string);
            setCountriesToGenerate(euCountries);
        } else {
            setCountriesToGenerate(data.value as string[]);
        }
    };

    const isGenerateDisabled = useMemo(() => {
        return (
            !yearToGenerate ||
            !formToGenerate ||
            !sectionToGenerate ||
            questionsToGenerate.length === 0 ||
            countriesToGenerate.length === 0
        );
    }, [
        formToGenerate,
        yearToGenerate,
        sectionToGenerate,
        questionsToGenerate.length,
        countriesToGenerate.length,
    ]);

    const handleGenerateTable = async () => {
        const tableDto: ITableDto = {
            guid: "",
            name: "",
            form: formToGenerate,
            year: yearToGenerate,
            section: sectionToGenerate,
            questions: questionsToGenerate,
            countries: countriesToGenerate,
        };

        setLoading(true);
        try {
            await createTable(tableDto);
            toast("Table generated successfully", true, 2000);
        } catch {
            toast("Error generating table", false, 2000);
        }
        setLoading(false);
        handleCloseGenerateModal();
        refreshTables();
    };

    const handleEditTable = async () => {
        if (!tableToEdit) {
            return;
        }
        setLoading(true);
        try {
            await editTable({
                ...tableToEdit,
                form: formToGenerate,
                year: yearToGenerate,
                section: sectionToGenerate,
                questions: questionsToGenerate,
                countries: countriesToGenerate,
            });
            toast("Table edited successfully", true, 2000);
        } catch {
            toast("Error editing table", false, 2000);
        }
        setLoading(false);
        handleCloseGenerateModal();
        refreshTables();
    };

    const handleCloseGenerateModal = () => {
        setFormToGenerate("");
        setYearToGenerate("");
        setSectionToGenerate("");
        setQuestionsToGenerate([]);
        setCountriesToGenerate([]);
        closeGenerateModal();
        setTableToEdit(undefined);
    };

    const handleDataChangeWarning = () => {
        setWarningModalOpen(false);

        switch (changeSource) {
            case "form":
                setFormToGenerate("");
                setYearToGenerate("");
                setSectionToGenerate("");
                break;
            case "year":
                setYearToGenerate("");
                setSectionToGenerate("");
                break;
            case "section":
                setSectionToGenerate("");
                break;
            default:
                break;
        }
        setQuestionsToGenerate([]);
        setCountriesToGenerate([]);
        setChangeSource("");
    };

    return (
        <>
            <Modal open={generateModalOpen} className="generate-modal" onClose={closeGenerateModal}>
                <Modal.Header>Generate table</Modal.Header>
                <Modal.Content>
                    <Form>
                        {tableToEdit && (
                            <Form.Field>
                                <label>Name</label>
                                <input
                                    value={tableToEdit.name}
                                    onChange={e =>
                                        setTableToEdit({ ...tableToEdit, name: e.target.value })
                                    }
                                />
                            </Form.Field>
                        )}
                        <Form.Field disabled={tableToEdit !== undefined && tableToEdit?.final}>
                            <label>Form</label>
                            <Select
                                fluid
                                clearable
                                value={formToGenerate}
                                options={formAsDropdownProps}
                                onChange={(_e, data) => {
                                    if (
                                        questionsToGenerate.length > 0 ||
                                        countriesToGenerate.length > 0
                                    ) {
                                        setWarningModalOpen(true);
                                        setChangeSource("form");
                                    } else {
                                        setFormToGenerate(data.value as string);
                                        setYearToGenerate("");
                                        setSectionToGenerate("");
                                    }
                                }}
                                loading={loading}
                                selectOnBlur={false}
                            />
                        </Form.Field>
                        <Form.Field
                            disabled={
                                formToGenerate === "" ||
                                (tableToEdit !== undefined && tableToEdit?.final)
                            }
                        >
                            <label>Year</label>
                            <Select
                                fluid
                                clearable
                                value={yearToGenerate}
                                options={yearAsDropdownProps}
                                onChange={(_e, data) => {
                                    if (
                                        questionsToGenerate.length > 0 ||
                                        countriesToGenerate.length > 0
                                    ) {
                                        setWarningModalOpen(true);
                                        setChangeSource("year");
                                    } else {
                                        setYearToGenerate(data.value as string);
                                        setSectionToGenerate("");
                                    }
                                }}
                                loading={loading}
                                selectOnBlur={false}
                            />
                        </Form.Field>
                        <Form.Field
                            disabled={
                                yearToGenerate === "" ||
                                (tableToEdit !== undefined && tableToEdit?.final)
                            }
                        >
                            <label>Sections</label>
                            <Select
                                fluid
                                clearable
                                value={sectionToGenerate}
                                options={sectionsAsDropdownProps}
                                onChange={(_e, data) => {
                                    if (
                                        questionsToGenerate.length > 0 ||
                                        countriesToGenerate.length > 0
                                    ) {
                                        setWarningModalOpen(true);
                                        setChangeSource("section");
                                    } else {
                                        setSectionToGenerate(data.value as string);
                                    }
                                }}
                                loading={loading}
                                selectOnBlur={false}
                            />
                        </Form.Field>
                        <Form.Field
                            disabled={
                                sectionToGenerate === "" ||
                                (tableToEdit !== undefined && tableToEdit?.final)
                            }
                        >
                            <label>Questions</label>
                            <Dropdown
                                className="newline"
                                fluid
                                multiple
                                search
                                selection
                                clearable
                                value={questionsToGenerate}
                                options={questionsAsDropdownProps}
                                onChange={(_e, data) => {
                                    setQuestionsToGenerate(data.value as string[]);
                                }}
                                loading={loading}
                                selectOnBlur={false}
                            />
                            {questionsToGenerate.length > 10 && (
                                <label className="warning-label">10 questions max</label>
                            )}
                        </Form.Field>
                        <Form.Field disabled={tableToEdit !== undefined && tableToEdit?.final}>
                            <label>Countries</label>
                            <Dropdown
                                fluid
                                multiple
                                search
                                selection
                                clearable
                                value={countriesToGenerate}
                                options={countriesAsDropdownProps}
                                onChange={(e, data) => handleSelectCountriesToGenerate(e, data)}
                                loading={loading}
                                selectOnBlur={false}
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={handleCloseGenerateModal} content="Cancel" />
                    <Popup
                        disabled={!isGenerateDisabled}
                        content="Please fill out all of the fields above"
                        trigger={
                            <span>
                                {tableToEdit === undefined ? (
                                    <Button
                                        primary
                                        onClick={handleGenerateTable}
                                        disabled={isGenerateDisabled}
                                        content="Generate"
                                    />
                                ) : (
                                    <Button primary onClick={handleEditTable} content="Edit" />
                                )}
                            </span>
                        }
                    />
                </Modal.Actions>
            </Modal>
            <WarningModal
                modalOpen={warningModalOpen}
                setModalOpen={setWarningModalOpen}
                warningText="Changing form will reset selected questions and countries. Are you sure you want to continue?"
                confirmAction={handleDataChangeWarning}
            />
        </>
    );
};
